// src/pages/OrderOnTheWay/services/paymentService.js
import { 
  writeBatch, 
  doc, 
  getDoc, 
  collection,
  query,
  where,
  getDocs 
} from 'firebase/firestore';
import { db, functions } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';
import bcrypt from 'bcryptjs';
import { 
  calculateOrderSettlement, 
  calculateSellerSettlement 
} from '../utils/calculationutil';

const OTP_EXPIRY_MINUTES = 10;
const USE_CASE = 'OPERATOR_PAYMENT_ISSUE_SELLER_JRE';

/* ============================================================================
   SELLER PAYMENT TRACK (parallel to `orderStatus` — `orderStatus` is never
   modified by the seller-payment flow, so the goods pipeline keeps moving):

     pending    → payment due (PRE_PICKUP / WAREHOUSE), not dispatched yet
     assigned   → settlement created, JRE assigned, handover OTP issued
     handedOver → operator verified the handover OTP; JRE carries the money
     delivered  → seller receipt OTP verified; seller is paid (terminal)

   Legacy values ('dispatched', 'verified', 'paid') written by older builds are
   still recognised by the helpers below.
============================================================================ */

export const SELLER_PAYMENT_STATUS = {
  PENDING: 'pending',
  ASSIGNED: 'assigned',
  HANDED_OVER: 'handedOver',
  DELIVERED: 'delivered',
};

/** Seller payment fully completed (any legacy or new marker). */
export const isSellerPaymentSettled = (order) =>
  !!order &&
  (order.isSellerPaid === true ||
    order.sellerPaymentVerified === true ||
    order.sellerPaymentStatus === 'verified' ||
    order.sellerPaymentStatus === 'paid' ||
    order.sellerPaymentStatus === 'delivered' ||
    order.earlySellerPaymentCompleted === true);

/** Seller payment already dispatched (assigned / in transit) but not settled. */
export const isSellerPaymentInFlight = (order) =>
  !!order &&
  !isSellerPaymentSettled(order) &&
  (order.sellerPaymentStatus === 'assigned' ||
    order.sellerPaymentStatus === 'handedOver' ||
    order.sellerPaymentStatus === 'dispatched');

/** JRE holds the money and is on the way to the seller. */
export const isSellerPaymentHandedOver = (order) =>
  !!order && order.sellerPaymentStatus === 'handedOver';

/**
 * Goods statuses used while an early seller payment travels to the seller.
 * Both casings are listed because different writers in the codebase have
 * produced `paymentDispatchedToSeller` and `paymentDispatchedtoSeller`.
 */
export const PAYMENT_LEG_ORDER_STATUSES = [
  'paymentDispatchedToSeller',
  'paymentDispatchedtoSeller',
];

/**
 * True when a PRE_PICKUP order is mid payment-leg: the seller payment has been
 * dispatched/handed over to the JRE, but the seller has not confirmed receipt
 * yet.
 *
 * While the money is in transit the goods `orderStatus` is parked on the payment
 * leg (see verifySellerPaymentHandover) and the JRE restores it to 'RTD' only
 * after the seller confirms receipt (see confirmOperatorToSellerPayment). So a
 * plain `orderStatus == 'RTD'` listing makes these orders vanish from the Order
 * Approved page for the whole duration of the payment. These orders belong on
 * that page until the payment passes.
 */
export const isPrePickupPaymentLegOrder = (order) => {
  if (!order) return false;
  if (order.paymentPreference !== 'PRE_PICKUP') return false;
  // Once the seller has confirmed receipt the payment has passed - drop it.
  if (isSellerPaymentSettled(order)) return false;
  const status = (order.orderStatus || '').toString().trim();
  return PAYMENT_LEG_ORDER_STATUSES.includes(status);
};

/**
 * Statuses the Order Approved page must listen to: RTD, plus the payment leg so
 * in-flight PRE_PICKUP orders stay listed.
 */
export const ORDER_APPROVED_ORDER_STATUSES = [
  'RTD',
  ...PAYMENT_LEG_ORDER_STATUSES,
];

/**
 * Whether an order from an ORDER_APPROVED_ORDER_STATUSES query belongs on the
 * Order Approved page.
 */
export const shouldShowInOrderApproved = (order) => {
  if (!order) return false;
  const status = (order.orderStatus || '').toString().trim();
  if (status === 'RTD') return true;
  return isPrePickupPaymentLegOrder(order);
};

// Find active sellerside JRE
export const findActiveJreWithMobile = async (operatorId) => {
  try {
    const jreStatusQuery = query(
      collection(db, 'jreStatus'),
      where('role', '==', 'sellerside'),
      where('operatorId', '==', operatorId),
      where('isOnline', '==', true)
    );
    const statusSnapshot = await getDocs(jreStatusQuery);
    if (statusSnapshot.empty) return null;

    const jreStatusDoc = statusSnapshot.docs[0];
    const jreId = jreStatusDoc.id;
    const jreStatusData = jreStatusDoc.data();

    const jreRegDoc = await getDoc(doc(db, 'jreregistrations', jreId));
    const primaryMobile = jreRegDoc.exists()
      ? jreRegDoc.data().primaryMobile?.toString().trim() || null
      : null;

    return {
      id: jreId,
      ...jreStatusData,
      primaryMobile,
    };
  } catch (err) {
    console.error('Error fetching JRE:', err);
    return null;
  }
};

// Assign JRE to deliver payment
export const handleAssignJre = async (seller, group, setAlertMessage) => {
  const operatorId = group.operatorId || group.orders[0]?.operatorId || localStorage.getItem('operatorId');
  if (!operatorId) {
    throw new Error('Operator ID missing. Please login again.');
  }

  try {
    // Find available sellerside JRE
    const jre = await findActiveJreWithMobile(operatorId);
    if (!jre) {
      throw new Error(`No active sellerside JRE found for operator: ${operatorId}`);
    }

    if (!jre.primaryMobile) {
      throw new Error(`JRE ${jre.id} has no registered mobile number.`);
    }

    const cleanMobile = jre.primaryMobile.replace(/\D/g, '');
    if (cleanMobile.length !== 10) {
      throw new Error(`Invalid mobile number for JRE ${jre.id}: ${jre.primaryMobile}`);
    }

    // Calculate total payment amount
    const totalPayment = group.orders.reduce((total, order) => {
      const amount = parseFloat(order.totalPaymentAmount) ||
        parseFloat(order.TotalAmount) || 0;
      return total + amount;
    }, 0);

    // Check JRE payment limits
    const currentValue = jre.currentValue || 0;
    const maxValue = jre.maxValue || Infinity;

    if (currentValue + totalPayment > maxValue) {
      throw new Error(
        `JRE ${jre.id} payment limit exceeded! Current: ₹${currentValue}, Limit: ₹${maxValue}, Required: ₹${totalPayment}`
      );
    }

    // Assign JRE to all orders in group
    const batch = writeBatch(db);
    group.orders.forEach((order) => {
      batch.update(doc(db, 'orderList', order.docId), {
        jreId: jre.id,
        operatorId: operatorId,
        paymentAssignedAt: new Date(),
        totalPaymentAmount: totalPayment,
      });
    });

    await batch.commit();

    return { success: true, jreId: jre.id, totalPayment };
  } catch (error) {
    console.error('JRE assignment error:', error);
    throw error;
  }
};

// Trigger OTP to JRE for payment issue / handover
export const handleVerifyPayment = async (seller, group, setAlertMessage) => {
  const jreId = group.jreId || group.orders[0]?.jreId;
  if (!jreId) {
    throw new Error('No JRE assigned for payment delivery.');
  }

  const operatorId = group.operatorId || group.orders[0]?.operatorId || localStorage.getItem('operatorId');
  if (!operatorId) {
    throw new Error('Operator ID missing. Please login again.');
  }

  // Get latest JRE data to check limits and fetch mobile
  const jreStatusDoc = await getDoc(doc(db, 'jreStatus', jreId));
  if (!jreStatusDoc.exists()) {
    throw new Error(`JRE ${jreId} not found.`);
  }

  const jre = { id: jreId, ...jreStatusDoc.data() };
  const jreRegDoc = await getDoc(doc(db, 'jreregistrations', jreId));
  const primaryMobile = jreRegDoc.exists()
    ? jreRegDoc.data().primaryMobile?.toString().trim() || null
    : null;

  if (!primaryMobile) {
    throw new Error(`JRE ${jreId} has no mobile number.`);
  }

  const cleanMobile = primaryMobile.replace(/\D/g, '');
  if (cleanMobile.length !== 10) {
    throw new Error(`Invalid mobile number for JRE ${jreId}`);
  }

  // Calculate total payment amount
  const totalPayment = group.orders.reduce((total, order) => {
    const amount = parseFloat(order.totalPaymentAmount) ||
      parseFloat(order.TotalAmount) || 0;
    return total + amount;
  }, 0);

  const currentValue = jre.currentValue || 0;
  const maxValue = jre.maxValue || Infinity;

  if (currentValue + totalPayment > maxValue) {
    throw new Error(`JRE ${jreId} payment limit exceeded! Current: ₹${currentValue}, Limit: ₹${maxValue}`);
  }

  try {
    const sendOTP = httpsCallable(functions, 'sendOTP');
    const result = await sendOTP({
      templateName: USE_CASE,
      useCase: USE_CASE,
      mobile: cleanMobile,
      validity: OTP_EXPIRY_MINUTES,
      params: {
        OPERATOR: operatorId,
      },
    });

    if (!result.data?.success) {
      throw new Error(result.data?.error || 'Failed to send OTP to JRE');
    }

    return {
      seller,
      group,
      otpId: result.data.otpId,
      mobile: cleanMobile,
      jreId: jre.id,
      operatorId,
      totalPayment,
      orderCount: group.orders.length,
      displayOtp: result.data.displayOtp || null,
    };
  } catch (error) {
    console.error('OTP generation error:', error);
    throw error;
  }
};

// Settle / Final Payment for Seller before assigning JRE
// settlementPayload - optional rich payload from PayModal (RTGS bucket, metal bucket, rates)
export const handleFinalPaymentForSeller = async (seller, group, setAlertMessage, settlementPayload = null) => {
  const operatorId = group.operatorId || group.orders[0]?.operatorId || localStorage.getItem('operatorId');
  if (!operatorId) {
    throw new Error('Operator ID missing. Please login again.');
  }

  // Calculate total payment and seller settlement
  const totalPayment = group.orders.reduce((total, order) => {
    const amount = parseFloat(order.totalPaymentAmount) ||
      parseFloat(order.TotalAmount) || 0;
    return total + amount;
  }, 0);

  const sellerSettlement = calculateSellerSettlement(group.orders);

  // Generate unique sellerSettlementId
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 6).toUpperCase();
  const sellerClean = (seller || 'SELLER').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 8);
  const sellerSettlementId = `STL-${sellerClean}-${timestamp}-${randomSuffix}`;

  const batch = writeBatch(db);

  // 1. Create document in 'settlements' collection
  // Merge rich PayModal payload (RTGS bucket, metal bucket, rates used, totals)
  const settlementDocRef = doc(db, 'settlements', sellerSettlementId);
  batch.set(settlementDocRef, {
    settlementId: sellerSettlementId,
    sellerSettlementId: sellerSettlementId,
    sellerId: seller,
    operatorId: operatorId,
    orderIds: group.orders.map((o) => o.docId || o.id),
    ordersCount: group.orders.length,
    totalPaymentAmount: settlementPayload?.totalAmountDue ?? settlementPayload?.totalAmount ?? totalPayment,
    totalFine: settlementPayload?.totalFineWeightDue ?? settlementPayload?.totalFine ?? sellerSettlement.fine ?? 0,
    totalAmount: settlementPayload?.totalAmountDue ?? settlementPayload?.totalAmount ?? totalPayment,
    sellerSettlement: sellerSettlement,
    status: 'settled',
    sellerPaymentStatus: 'pending',
    sellerPaymentVerified: false,
    settledAt: new Date(),
    createdAt: new Date(),
    // Rich payment details from PayModal (if provided)
    ...(settlementPayload ? {
      totalFineWeightDue:   settlementPayload.totalFineWeightDue   || settlementPayload.totalFine || 0,
      totalAmountDue:       settlementPayload.totalAmountDue       || settlementPayload.totalAmount || 0,
      totalFineWtDelivered: settlementPayload.totalFineWtDelivered || 0,
      totalAmountPaid:      settlementPayload.totalAmountPaid      || 0,
      rtgsBucket:           settlementPayload.rtgsBucket           || null,
      metalBucket:          settlementPayload.metalBucket          || null,
      ratesUsed:            settlementPayload.ratesUsed            || null,
    } : {}),
  });

  // 2. Update each order in orderList at top level with settlement data & sellerSettlementId
  group.orders.forEach((order) => {
    const orderRef = doc(db, 'orderList', order.docId);
    const orderSettlement = calculateOrderSettlement(order);
    batch.update(orderRef, {
      sellerSettlementId: sellerSettlementId,
      settlementId: sellerSettlementId,
      settlementData: {
        ...orderSettlement,
        // Attach bucket info so each order knows how payment was split
        ...(settlementPayload ? {
          rtgsBucket:   settlementPayload.rtgsBucket   || null,
          metalBucket:  settlementPayload.metalBucket  || null,
          ratesUsed:    settlementPayload.ratesUsed    || null,
        } : {}),
      },
      settledAt: new Date(),
      isSettled: true,
      operatorId: operatorId,
      totalPaymentAmount: totalPayment,
    });
  });

  await batch.commit();

  return { success: true, settlementId: sellerSettlementId, totalPayment };
};

/**
 * The goods status an early seller payment must return to once the seller
 * confirms receipt:
 *   PRE_PICKUP → RTD        (order continues as a pre-pickup order)
 *   WAREHOUSE  → InWarehouse
 * Returns null for post-delivery (buyer-paid) payments so those settle
 * terminally as PaymentSettled.
 */
const getSellerPaymentReturnStatus = (orders, fallbackStatus = null) => {
  const firstOrder = orders?.[0] || {};
  if (fallbackStatus) return fallbackStatus;
  if (firstOrder.paymentPreference === 'PRE_PICKUP') return 'RTD';
  if (firstOrder.paymentPreference === 'WAREHOUSE') return 'InWarehouse';
  return null;
};

/**
 * Re-issue the handover OTP for a group whose payment is still `assigned`
 * (e.g. the operator closed the verification modal before confirming).
 */
export const resendSellerPaymentHandoverOtp = async (groupOrPayload) => {
  const payload = groupOrPayload || {};
  const orders = payload.orders || payload.group?.orders || [];
  const jreId =
    payload.jreId ||
    payload.group?.jreId ||
    orders[0]?.sellerPaymentJreId ||
    orders[0]?.jreId;
  if (!jreId) {
    throw new Error('No JRE assigned for this payment yet.');
  }

  const operatorId =
    payload.operatorId ||
    payload.group?.operatorId ||
    orders[0]?.operatorId ||
    localStorage.getItem('operatorId');
  if (!operatorId) {
    throw new Error('Operator ID missing. Please login again.');
  }

  const cleanMobile = await fetchJreMobile(jreId);
  const otpData = await sendHandoverOtp(cleanMobile, operatorId);

  const totalPayment = payload.totalPayment ||
    orders.reduce(
      (sum, o) =>
        sum +
        (parseFloat(o.totalPaymentAmount) ||
          parseFloat(o.settlementData?.amount) ||
          0),
      0,
    );

  return {
    otpId: otpData.otpId,
    mobile: cleanMobile,
    operatorId,
    jreId,
    totalPayment,
    seller:
      payload.seller ||
      payload.group?.sellerId ||
      orders[0]?.sellerId ||
      payload.group?.groupId ||
      '',
    orderCount: orders.length,
    group: payload.group || payload,
    displayOtp: otpData.displayOtp || null,
    settlementId:
      payload.settlementId ||
      orders[0]?.sellerSettlementId ||
      orders[0]?.settlementId ||
      '',
  };
};

/**
 * STEP 2 — Operator verifies the handover OTP; the money is considered
 * handed over to the JRE. The order's goods `orderStatus` is untouched.
 */
export const verifySellerPaymentHandover = async (otpInput, modalData) => {
  const { otpId, group, jreId, operatorId, totalPayment, seller, mobile } = modalData || {};
  if (!otpId) {
    throw new Error('otpId is required');
  }

  // 1. Verify OTP via the cloud function (fallback: bcrypt against the otps doc)
  let isOtpValid = false;
  try {
    const verifyOTP = httpsCallable(functions, 'verifyOTP');
    const res = await verifyOTP({
      otpId,
      otp: otpInput,
      mobile,
      useCase: USE_CASE,
    });
    isOtpValid = !!res.data?.success;
  } catch (cfErr) {
    console.warn('verifyOTP cloud call failed, trying direct check:', cfErr);
    const snap = await getDoc(doc(db, 'otps', otpId));
    if (snap.exists()) {
      const data = snap.data();
      const hashToCompare = data.otp || data.code;
      if (hashToCompare && (await bcrypt.compare(String(otpInput).trim(), hashToCompare))) {
        isOtpValid = true;
      } else {
        throw new Error('Invalid OTP. Please try again.');
      }
    } else {
      throw cfErr;
    }
  }
  if (!isOtpValid) {
    throw new Error('OTP verification failed.');
  }

  // 2. Guarded per-order update — only orders still awaiting handover
  const candidateIds = (group?.orders || [])
    .map((o) => o.docId || o.id)
    .filter(Boolean);
  const now = new Date();
  const batch = writeBatch(db);
  let updated = 0;
  let settlementId = modalData.settlementId || '';

  for (const id of candidateIds) {
    const ref = doc(db, 'orderList', id);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const data = snap.data();
    if (data.sellerPaymentStatus !== SELLER_PAYMENT_STATUS.ASSIGNED) continue; // idempotency guard
    if (!settlementId) {
      settlementId = data.sellerSettlementId || data.settlementId || '';
    }
    // The money is now physically with the seller-side JRE, so bridge the
    // operator payment track into the JRE SellDeliver app. The JRE app only
    // lists orders by `jreId` + `orderStatus` (see selldeliver useOrdersData),
    // so once the handover OTP is verified we link the JRE and move the goods
    // onto the payment-delivery leg ('paymentDispatchedToSeller' →
    // operator_to_seller_payment → "Deliver Payment To Seller").
    // Once the seller confirms receipt the order returns to its goods stage
    // (PRE_PICKUP → RTD, WAREHOUSE → InWarehouse) so the goods pipeline keeps
    // flowing, while the payment is recorded as settled on the sellerPayment
    // track (see confirmOperatorToSellerPayment).
    const assignedJre = jreId || data.sellerPaymentJreId || data.jreId || '';

    // Goods status the order must continue in once the seller confirms receipt
    // (PRE_PICKUP → RTD, WAREHOUSE → InWarehouse).
    const returnStatus =
      data.sellerPaymentReturnStatus ||
      data.sellerPaymentOriginalStatus ||
      data.orderStatus ||
      null;

    batch.update(ref, {
      sellerPaymentStatus: SELLER_PAYMENT_STATUS.HANDED_OVER,
      sellerPaymentHandedOverAt: now,
      sellerPaymentHandedOverBy: operatorId || null,
      sellerPaymentOtpId: otpId,
      paymentDispatchedToSellerAt: now,
      otpReference: otpId,
      lastSellerPaymentUpdateAt: now,
      // ── Bridge to the JRE SellDeliver app (visible only after handover) ──
      jreId: assignedJre,
      sellerPaymentJreId: assignedJre,
      // PRE_PICKUP → goods aren't picked up yet, so move the order onto the
      // payment-delivery leg (JRE app: operator_to_seller_payment).
      // WAREHOUSE  → goods are still in the warehouse, so KEEP 'InWarehouse'.
      // That keeps the row (and its locked Dispatch button) visible in the
      // In Warehouse page until the payment is settled; the JRE app still picks
      // it up via `sellerPaymentStatus = 'handedOver'`.
      ...(returnStatus === 'InWarehouse'
        ? {}
        : { orderStatus: 'paymentDispatchedToSeller' }),
      sellerPaymentReturnStatus: returnStatus,
    });
    updated += 1;
  }

  if (updated === 0) {
    throw new Error('This payment was already handed over or settled.');
  }

  // 3. Audit trail
  const historyRef = doc(collection(db, 'history'));
  batch.set(historyRef, {
    historyId: historyRef.id,
    action: 'SELLER_PAYMENT_HANDED_TO_JRE',
    settlementId,
    sellerId: seller || '',
    operatorId: operatorId || '',
    jreId: jreId || '',
    orderIds: candidateIds,
    ordersCount: candidateIds.length,
    totalPaymentAmount: totalPayment || 0,
    otpId,
    timestamp: now,
    createdAt: now,
  });

  await batch.commit();

  return {
    success: true,
    settlementId,
    updated,
    message: `Payment of ₹${parseFloat(totalPayment || 0).toFixed(2)} handed over to JRE ${jreId}. The JRE will deliver it to the seller against the receipt OTP.`,
  };
};


// Shared helper — resolve a JRE's 10-digit mobile number
const fetchJreMobile = async (jreId) => {
  const jreRegDoc = await getDoc(doc(db, 'jreregistrations', jreId));
  const primaryMobile = jreRegDoc.exists()
    ? jreRegDoc.data().primaryMobile?.toString().trim() || null
    : null;
  if (!primaryMobile) {
    throw new Error(`JRE ${jreId} has no registered mobile number.`);
  }
  const cleanMobile = primaryMobile.replace(/\D/g, '');
  if (cleanMobile.length !== 10) {
    throw new Error(`Invalid mobile number for JRE ${jreId}`);
  }
  return cleanMobile;
};

// Shared helper — issue the operator→JRE handover OTP
const sendHandoverOtp = async (cleanMobile, operatorId) => {
  const sendOTP = httpsCallable(functions, 'sendOTP');
  const result = await sendOTP({
    templateName: USE_CASE,
    useCase: USE_CASE,
    mobile: cleanMobile,
    validity: OTP_EXPIRY_MINUTES,
    params: { OPERATOR: operatorId },
  });
  if (!result.data?.success) {
    throw new Error(result.data?.message || 'Failed to send handover OTP to JRE');
  }
  return result.data;
};

/**
 * STEP 1 (PRE_PICKUP & WAREHOUSE seller payments)
 * Assigns an active sellerside JRE for the seller payment, creates the
 * settlement document and issues the handover OTP the operator must confirm.
 *
 * IMPORTANT: `orderStatus` is intentionally NOT modified — the goods pipeline
 * (RTD → Assigned → PickedUp → InWarehouse → Dispatched → …) keeps moving in
 * parallel. The payment progresses on its own `sellerPaymentStatus` track.
 */
export const handleDispatchSellerPaymentWithJre = async (
  seller,
  group,
  setAlertMessage,
  settlementPayload = null,
  options = {},
) => {
  if (!group || !Array.isArray(group.orders) || group.orders.length === 0) {
    throw new Error('No orders in this payment group.');
  }

  const operatorId = group.operatorId || group.orders[0]?.operatorId || localStorage.getItem('operatorId');
  if (!operatorId) {
    throw new Error('Operator ID missing. Please login again.');
  }

  // Guard: never double-dispatch an order that is already in flight or settled
  const pendingOrders = group.orders.filter(
    (o) => !isSellerPaymentSettled(o) && !isSellerPaymentInFlight(o),
  );
  if (pendingOrders.length === 0) {
    throw new Error('Seller payment for this group is already assigned or settled.');
  }

  const jre = await findActiveJreWithMobile(operatorId);
  if (!jre) {
    throw new Error(`No active sellerside JRE found for operator: ${operatorId}`);
  }

  const cleanMobile = await fetchJreMobile(jre.id);

  // 1. Settlement computation + settlement document (unchanged behaviour)
  const settlement = await handleFinalPaymentForSeller(
    seller,
    { ...group, orders: pendingOrders },
    setAlertMessage,
    settlementPayload,
  );

  // 2. Handover OTP — the operator must verify this before the money moves
  const otpData = await sendHandoverOtp(cleanMobile, operatorId);

  // After the seller confirms receipt the order continues in its goods stage:
  //   PRE_PICKUP → RTD          (order keeps going as a pre-pickup order)
  //   WAREHOUSE  → InWarehouse
  // We record that return status here so the JRE app's
  // confirmOperatorToSellerPayment restores it (instead of settling the order
  // terminally), which keeps the goods pipeline flowing.
  const returnStatus = getSellerPaymentReturnStatus(
    pendingOrders,
    options.returnStatus,
  );
  const stage = options.stage || pendingOrders[0]?.paymentPreference || 'BUYER_PAYMENT';
  const now = new Date();
  const batch = writeBatch(db);

  pendingOrders.forEach((order) => {
    const orderRef = doc(db, 'orderList', order.docId || order.id);
    batch.update(orderRef, {
      // ── Payment track — `orderStatus`/`jreId` are intentionally NOT touched
      sellerPaymentStatus: SELLER_PAYMENT_STATUS.ASSIGNED,
      sellerPaymentStage: stage,
      sellerPaymentJreId: jre.id,
      sellerPaymentAssignedAt: now,
      sellerPaymentOtpId: otpData.otpId,
      sellerPaymentReturnStatus: returnStatus, // audit + legacy consumers
      sellerPaymentOriginalStatus: order.orderStatus, // audit only
      sellerPaymentTransport: options.transport || 'jre',
      // ── Settlement ───────────────────────────────────────────────────────
      sellerSettlementId: settlement.settlementId,
      settlementId: settlement.settlementId,
      sellerPaymentVerified: false,
      paymentAssignedAt: now,
      totalPaymentAmount: settlement.totalPayment,
      operatorId,
      lastSellerPaymentUpdateAt: now,
    });
  });

  await batch.commit();

  return {
    success: true,
    settlementId: settlement.settlementId,
    jreId: jre.id,
    returnStatus,
    stage,
    totalPayment: settlement.totalPayment,
    pendingCount: pendingOrders.length,
    // modal payload for OtpVerificationModal + verifySellerPaymentHandover
    otp: {
      otpId: otpData.otpId,
      mobile: cleanMobile,
      operatorId,
      jreId: jre.id,
      totalPayment: settlement.totalPayment,
      seller,
      orderCount: pendingOrders.length,
      group: { orders: pendingOrders },
      displayOtp: otpData.displayOtp || null,
      settlementId: settlement.settlementId,
    },
  };
};

// Verify JRE OTP, create history collection data with settlement data, and update all orders
export const verifyPaymentOtp = async (otpInput, modalData) => {
  const { otpId, group, jreId, operatorId, totalPayment, seller, mobile } = modalData;

  try {
    // 1. Verify OTP using Cloud Function verifyOTP
    let isOtpValid = false;
    try {
      const verifyOTP = httpsCallable(functions, 'verifyOTP');
      const verifyRes = await verifyOTP({
        otpId,
        otp: otpInput,
        mobile,
        useCase: USE_CASE,
      });
      if (verifyRes.data?.success) {
        isOtpValid = true;
      }
    } catch (cfErr) {
      console.warn('verifyOTP Cloud Function error, attempting fallback direct check:', cfErr);
      // Fallback: check otps collection directly if available
      const otpRef = doc(db, 'otps', otpId);
      const otpSnap = await getDoc(otpRef);
      if (otpSnap.exists()) {
        const otpData = otpSnap.data();
        const hashToCompare = otpData.otp || otpData.code;
        if (hashToCompare && await bcrypt.compare(otpInput.toString().trim(), hashToCompare)) {
          isOtpValid = true;
        } else {
          throw new Error('Invalid OTP. Please try again.');
        }
      } else {
        throw cfErr;
      }
    }

    if (!isOtpValid) {
      throw new Error('OTP verification failed.');
    }

    // 2. Reuse or generate sellerSettlementId
    const existingSettlementId =
      group.settlementId ||
      group.sellerSettlementId ||
      group.orders?.[0]?.sellerSettlementId ||
      group.orders?.[0]?.settlementId;

    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2, 6).toUpperCase();
    const sellerClean = (seller || 'SELLER').replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 8);
    const sellerSettlementId = existingSettlementId || `STL-${sellerClean}-${timestamp}-${randomSuffix}`;

    // 3. Calculate seller settlement summary
    const sellerSettlement = calculateSellerSettlement(group.orders);

    const batch = writeBatch(db);

    // 4. Add data to 'history' collection with settlement data
    const historyDocRef = doc(collection(db, 'history'));
    batch.set(historyDocRef, {
      historyId: historyDocRef.id,
      action: 'PAYMENT_DISPATCHED_TO_SELLER',
      sellerSettlementId: sellerSettlementId,
      settlementId: sellerSettlementId,
      sellerId: seller,
      operatorId: operatorId || group.orders[0]?.operatorId || '',
      jreId: jreId,
      orderIds: group.orders.map((o) => o.docId || o.id),
      ordersCount: group.orders.length,
      totalPaymentAmount: totalPayment,
      totalFine: sellerSettlement.fine || 0,
      settlementData: sellerSettlement,
      previousStatus: 'paymentDispatchedtoOperator',
      newStatus: 'paymentDispatchedToSeller',
      timestamp: new Date(),
      createdAt: new Date(),
    });

    // 5. Create or update document in 'settlements' collection
    const settlementDocRef = doc(db, 'settlements', sellerSettlementId);
    batch.set(settlementDocRef, {
      settlementId: sellerSettlementId,
      sellerSettlementId: sellerSettlementId,
      sellerId: seller,
      operatorId: operatorId || group.orders[0]?.operatorId || '',
      jreId: jreId,
      orderIds: group.orders.map((o) => o.docId || o.id),
      ordersCount: group.orders.length,
      totalPaymentAmount: totalPayment,
      totalFine: sellerSettlement.fine || 0,
      sellerSettlement: sellerSettlement,
      status: 'paymentDispatchedToSeller',
      sellerPaymentStatus: 'pending',
      sellerPaymentVerified: false,
      dispatchedToSellerAt: new Date(),
      createdAt: new Date(),
    }, { merge: true });

    // 6. Update each order in orderList at top level
    group.orders.forEach((order) => {
      const orderRef = doc(db, 'orderList', order.docId);
      const orderSettlement = calculateOrderSettlement(order);
      batch.update(orderRef, {
        orderStatus: 'paymentDispatchedToSeller',
        sellerSettlementId: sellerSettlementId,
        settlementId: sellerSettlementId,
        settlementData: orderSettlement,
        paymentDispatchedToSellerAt: new Date(),
        jreId: jreId,
        operatorId: operatorId || order.operatorId || '',
      });
    });

    // 7. Update JRE current value
    const jreDoc = await getDoc(doc(db, 'jreStatus', jreId));
    if (jreDoc.exists()) {
      const jreData = jreDoc.data();
      const currentValue = jreData.currentValue || 0;
      batch.update(doc(db, 'jreStatus', jreId), {
        currentValue: currentValue + totalPayment,
      });
    }

    await batch.commit();
    return { success: true, settlementId: sellerSettlementId };
  } catch (error) {
    console.error('Payment verification error:', error);
    throw error;
  }
};
